import { EmailInfo } from '@aibees/elephant/feature/search-table-people/components/people-email-popover/components/email-info';
import { UnlockEmails } from '@aibees/elephant/feature/search-table-people/components/people-email-popover/components/unlock-emails';
import { useHandleUnlockEmails } from '@aibees/elephant/feature/search-table-people/components/people-email-popover/hooks/use-handle-unlock-emails';
import {
  getEmailKind,
  howManyEmailsFound,
} from '@aibees/elephant/feature/search-table-people/components/people-email-popover/utils';
import { useEmailEnrichmentDescriptionText } from '@aibees/elephant/hooks/people/use-email-enrichment-description-text';
import { BouncebanChip, CardBox, Icon } from '@aibees/shared/components';
import { useCreditUsageItem } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import * as Styled from './styles';
import * as SX from './sx';
import type { PersonEmailInfoProps as Props } from './types';

export const PersonEmailInfo = memo(({ className, emails, data }: Props) => {
  const emailsFound = howManyEmailsFound(emails);
  const {
    unlocked,
    showReverify,
    hasUpdate,
    needToCheckForUpdate,
    creditType,
  } = useHandleUnlockEmails(data);
  const descriptionText = useEmailEnrichmentDescriptionText(data);
  const { credit } = useCreditUsageItem(creditType);

  return (
    <Styled.Wrapper
      className={classnames('PersonEmailInfo', className)}
      sx={SX.root}
    >
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body3" color="grey.700" fontWeight={600}>
          Email Address{' '}
        </Typography>
        <UnlockEmails compact item={data} />
      </Stack>
      {needToCheckForUpdate && (
        <Alert
          variant="outlined"
          severity="default"
          icon={<Icon name="info-circle-outline" size={18} color="grey.600" />}
          sx={SX.alert}
        >
          <Typography variant="body4" color="grey.600" fontWeight={500}>
            {`Job Change detected - Click Check for Update (${credit}x credits) to refresh the new company and automatically search for a current work email (email charged only if found).`}
          </Typography>
        </Alert>
      )}
      <Stack
        direction="row"
        gap={0.5}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body3" color="grey.500" fontWeight={400}>
          {descriptionText}
        </Typography>
        <BouncebanChip />
      </Stack>

      <CardBox sx={SX.card} contentSx={SX.cardContent}>
        {emails?.map((emailData, i) => (
          <EmailInfo
            key={i.toString()}
            data={emailData}
            kind={getEmailKind(i, true)}
            unlocked={!!unlocked}
            reverify={!!showReverify}
            hasUpdate={hasUpdate}
            needToCheckForUpdate={needToCheckForUpdate}
          />
        ))}
      </CardBox>
      {unlocked && !emailsFound && (
        <Box display="flex" gap={0.5} alignItems="center">
          <Icon name="info-circle-outline" size={14} color="grey.500" />
          <Typography variant="body4" color="grey.600" fontWeight={400}>
            No valid email was found & no credits were deducted.
          </Typography>
        </Box>
      )}
    </Styled.Wrapper>
  );
});

PersonEmailInfo.displayName = 'PersonEmailInfo';
